import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Zap, Gift, Wallet } from "lucide-react";
import AppHeader from "../components/AppHeader";
import CreditPacks from "../components/CreditPacks";
import CustomTopUp from "../components/CustomTopUp";
import { useCredits } from "../context/CreditsContext";
import { api } from "../lib/api";
import { buyCredits } from "../lib/buyCredits";
import { modelBadge } from "../components/ModelSelect";

const KIND = { monthly_grant: "Monthly free credits", generation: "AI build", refund: "Refund", purchase: "Credit purchase", admin_grant: "Granted by admin", manual_payment: "Payment recorded", domain: "Custom domain" };

const Stat = ({ icon: Icon, label, value, hint, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={testid}>
    <div className="flex items-center gap-2 text-sm text-slate-500"><Icon size={15} className="text-emerald-600" /> {label}</div>
    <div className="mt-3 font-display text-4xl font-bold text-slate-900">{value ?? "—"}</div>
    {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
  </div>
);

export default function Billing() {
  const { credits, refresh, models } = useCredits();
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(null);
  const loadHistory = useCallback(() => api.creditHistory().then(setHistory).catch(() => {}), []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const buy = (sel) => {
    setBusy(typeof sel === "string" ? sel : "custom");
    buyCredits(sel, {
      onSuccess: (r) => { toast.success(`${r.credits_added || 0} credits added`); refresh(); loadHistory(); setBusy(null); },
      onError: (m) => { toast.error(m); setBusy(null); },
      onDismiss: () => setBusy(null),
    });
  };

  return (
    <div className="min-h-screen bg-[#F5F8FC]" data-testid="billing-page">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-14">
        <section>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Billing & credits</h1>
          <p className="mt-2 text-slate-500">{credits?.monthly_allowance ? "Free credits refill on the 1st of every month. Paid credits never expire and are used after your free ones." : "Buy credits whenever you need them. Paid credits never expire."}</p>
          <div className={`mt-8 grid gap-4 ${credits?.monthly_allowance ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
            <Stat icon={Zap} label="Total available" value={credits?.total} testid="billing-total" />
            {credits?.monthly_allowance > 0 && (
              <Stat icon={Gift} label="Free this month" value={`${credits.free}/${credits.monthly_allowance}`}
                hint={`Refills ${new Date(credits.resets_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`} testid="billing-free" />
            )}
            <Stat icon={Wallet} label="Paid credits" value={credits?.paid} hint="Never expire" testid="billing-paid" />
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Top up</h2>
          <div className="mb-6 flex flex-wrap gap-2">
            {models.map((m) => <span key={m.id} className={`px-2 py-1 rounded border text-xs font-mono ${modelBadge(m.id)}`}>{m.label}: {m.credits} cr / message</span>)}
          </div>
          <CreditPacks onBuy={buy} busyId={busy} />
          <CustomTopUp onBuy={buy} busy={busy === "custom"} />
          <p className="mt-4 text-xs text-slate-400">Payments are processed securely by Razorpay (UPI, cards, netbanking). Prices include all taxes.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Usage history</h2>
          <div className="rounded-2xl border border-slate-200 overflow-hidden" data-testid="billing-history">
            {history.length === 0 ? (
              <p className="p-6 text-sm text-slate-500">No activity yet.</p>
            ) : history.map((h) => (
              <div key={h.id} className="flex items-center gap-4 px-5 py-3 border-b border-slate-200/80 last:border-0 text-sm" data-testid="billing-history-row">
                <span className="text-slate-600 flex-1 min-w-0 truncate">{KIND[h.kind] || h.kind}<span className="text-slate-400"> · {h.note}</span></span>
                <span className="text-xs text-slate-400 hidden sm:block">{new Date(h.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
                <span className={`font-mono w-14 text-right ${h.delta > 0 ? "text-emerald-600" : "text-slate-500"}`}>{h.delta > 0 ? "+" : ""}{h.delta}</span>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
